import React, { useState } from 'react'
import styled from 'styled-components'
import { MenuProps } from 'antd'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { NAMESPACE } from 'locale'
import { procedureConstants } from 'locale/procedures'
import {
  EmptyState,
  Spinner,
  NavigationModal,
  Heading,
  ExpandableCard,
  ExpandableCardField
} from '@lmc/ui-kit'
import { Procedure } from '../types'
import { formatDateTime } from '../utils/procedureUtils'
import { useProcedureArchive } from '../hooks/useProcedureArchive'
import useRoleAccess from '../../../../../components/AccessControl/useRoleAccess'
import { UserRole } from '@lmc/ts-common'
import { useInfiniteScrollFetch } from '../../../../../hooks/useInfiniteScroll'

interface ProcedureListProps {
  procedures: Procedure[]
  isLoading: boolean
  onEdit: (procedure: Procedure) => void
  onRefetch: () => void
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

const STATUS_TAG_COLORS: Record<string, string> = {
  active: 'green',
  archived: 'default'
}

export const ProcedureList: React.FC<ProcedureListProps> = ({
  procedures,
  isLoading,
  onEdit,
  onRefetch,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage
}) => {
  const { t } = useTranslation(NAMESPACE.PROCEDURES)
  const { id: residentId } = useParams<{ id: string }>()
  const [expandedProcedureId, setExpandedProcedureId] = useState<string | null>(
    null
  )
  const [archiveModalVisible, setArchiveModalVisible] = useState(false)
  const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(
    null
  )

  const { archiveProcedure, unarchiveProcedure } = useProcedureArchive(
    residentId || '',
    () => {
      onRefetch()
      setArchiveModalVisible(false)
      setSelectedProcedure(null)
    }
  )

  const { infiniteRef, rootRef } = useInfiniteScrollFetch({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  })

  const hasEditAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Procedure'
      }
    ]
  )

  const hasArchiveAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'delete',
        this: 'Procedure'
      }
    ]
  )

  if (isLoading) {
    return <Spinner />
  }

  if (procedures.length === 0) {
    return (
      <EmptyState
        title={t(procedureConstants.NO_PROCEDURES_FOUND)}
        description={t(procedureConstants.NO_PROCEDURES_DESCRIPTION)}
      />
    )
  }

  const activeProcedures = procedures.filter((p) => p.status === 'active')
  const archivedProcedures = procedures.filter((p) => p.status === 'archived')

  const toggleExpand = (procedureId: string) => {
    setExpandedProcedureId(
      expandedProcedureId === procedureId ? null : procedureId
    )
  }

  const handleArchiveClick = (procedure: Procedure) => {
    setSelectedProcedure(procedure)
    setArchiveModalVisible(true)
  }

  const handleConfirmArchive = async () => {
    if (!selectedProcedure) return

    if (selectedProcedure.status === 'active') {
      await archiveProcedure(selectedProcedure._id)
    } else {
      await unarchiveProcedure(selectedProcedure._id)
    }
  }

  const getMenuItems = (procedure: Procedure): MenuProps['items'] => {
    const items: MenuProps['items'] = []

    if (hasEditAccess && procedure.status === 'active') {
      items.push({
        key: 'edit',
        label: t(procedureConstants.EDIT),
        onClick: () => onEdit(procedure)
      })
    }

    if (hasArchiveAccess) {
      items.push({
        key: 'archive',
        label:
          procedure.status === 'active'
            ? t(procedureConstants.ARCHIVE)
            : t(procedureConstants.UN_ARCHIVE),
        onClick: () => handleArchiveClick(procedure)
      })
    }

    return items
  }

  const getStatusLabel = (status: string) => {
    return status === 'active'
      ? t(procedureConstants.ACTIVE)
      : t(procedureConstants.ARCHIVED)
  }

  const buildFields = (procedure: Procedure): ExpandableCardField[] => {
    const fields: ExpandableCardField[] = []

    if (procedure.description) {
      fields.push({
        label: t(procedureConstants.DESCRIPTION_OF_PROCEDURE),
        value: procedure.description,
        fullWidth: true
      })
    }

    if (procedure.createdBy?.name) {
      fields.push({
        label: t(procedureConstants.CREATED_BY),
        value: `${procedure.createdBy.name}${
          procedure.createdAt
            ? ` on ${formatDateTime(procedure.createdAt)}`
            : ''
        }`
      })
    }

    if (procedure.updatedBy?.name) {
      fields.push({
        label: t(procedureConstants.UPDATED_BY),
        value: `${procedure.updatedBy.name}${
          procedure.updatedAt
            ? ` on ${formatDateTime(procedure.updatedAt)}`
            : ''
        }`
      })
    }

    return fields
  }

  const renderProcedure = (procedure: Procedure) => {
    const dateTimeText = formatDateTime(procedure.procedureDateTime)
    const menuItems = getMenuItems(procedure)
    const statusTagColor =
      STATUS_TAG_COLORS[procedure.status] || STATUS_TAG_COLORS.active
    const fields = buildFields(procedure)

    return (
      <ExpandableCard
        key={procedure._id}
        id={procedure._id}
        title={procedure.title}
        status={
          procedure.status === 'archived'
            ? {
                label: getStatusLabel(procedure.status),
                color: statusTagColor
              }
            : undefined
        }
        date={
          dateTimeText
            ? `Procedure: ${dateTimeText}`
            : t(procedureConstants.NO_DATE_TIME_SPECIFIED)
        }
        fields={fields}
        menuItems={menuItems}
        isExpanded={expandedProcedureId === procedure._id}
        onToggleExpand={fields.length > 0 ? toggleExpand : undefined}
        testId={`procedure-${procedure._id}`}
      />
    )
  }

  return (
    <>
      <ScrollContainer ref={rootRef}>
        <ListContainer>
          {activeProcedures.map(renderProcedure)}

          {archivedProcedures.length > 0 && (
            <>
              <SectionHeading>
                <Heading level={4}>
                  {t(procedureConstants.ARCHIVED_PROCEDURES)}
                </Heading>
              </SectionHeading>
              {archivedProcedures.map(renderProcedure)}
            </>
          )}

          {(hasNextPage || isFetchingNextPage) && (
            <LoadMoreContainer ref={infiniteRef}>
              {isFetchingNextPage && <Spinner />}
            </LoadMoreContainer>
          )}
        </ListContainer>
      </ScrollContainer>

      <NavigationModal
        showPrompt={archiveModalVisible}
        confirmNavigation={handleConfirmArchive}
        cancelNavigation={() => {
          setArchiveModalVisible(false)
          setSelectedProcedure(null)
        }}
        modalTitle={
          selectedProcedure?.status === 'active'
            ? t(procedureConstants.ARCHIVE_PROCEDURE)
            : t(procedureConstants.RESTORE_PROCEDURE)
        }
        modalContentText={
          selectedProcedure?.status === 'active'
            ? t(procedureConstants.ARCHIVE_CONFIRMATION)
            : t(procedureConstants.RESTORE_CONFIRMATION)
        }
        modalConfirmText={
          selectedProcedure?.status === 'active'
            ? t(procedureConstants.ARCHIVE)
            : t(procedureConstants.RESTORE)
        }
        modalCancelText={t(procedureConstants.CANCEL)}
      />
    </>
  )
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px auto 0px auto;
  padding-bottom: 40px;
  max-width: 1080px;
`

const SectionHeading = styled.div`
  margin-top: 32px;
  margin-bottom: 16px;
`

const ScrollContainer = styled.div`
  overflow-y: auto;
  height: 100%;
`

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`
